<template>
  <form-section>
    <rest-list-editable
      ref="rest-list-editable"
      :payload="fetchEndpoint"
      :rules="rules"
      :save-warning="channelsUpdated"
      :add-endpoint="addEndpoint"
      :update-endpoint="updateEndpoint"
      @row-updated="onRowUpdated"
      @row-discarded="onRowDiscard"
      :before-save="beforeSaveRow"
    >
      <rest-list-column
        prop="name"
        :fixed="true"
        :width="300"
        :editable="true"
        :title="$t('SubscriptionPreference_Field_Name')"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <span v-if="model.id">{{ model.name }}</span>
          <el-input
            v-model="model.name"
            v-else
            @keydown.native="handleTabKey"
          />
        </template>
      </rest-list-column>

      <rest-list-column
        prop="display_name"
        :editable="true"
        :width="300"
        :title="$t('SubscriptionPreference_Field_DisplayName')"
      >
        <template slot-scope="{ scope }" slot="default">
          <span v-if="scope.row.isDisplayNamesDifferent">
            <el-tag type="warning" size="mini">
              {{
                $t(
                  'ConsentManager_Custom_Channels_DisplayName_UpdatePlaceholder'
                )
              }}
            </el-tag>
            <el-tooltip placement="right">
              <div
                class="ConsentNameInfo--Tooltip"
                slot="content"
                v-html="
                  $t(
                    'ConsentManager_Custom_Channels_DisplayName_UpdatePlaceholder_Description'
                  )
                "
              />
              <span><i class="el-icon-warning-outline"></i></span>
            </el-tooltip>
          </span>
          <span v-else> {{ scope.row.display_name }}</span>
        </template>
        <template slot="input-edit" slot-scope="{ model }">
          <el-input
            value=""
            v-model="displayName"
            v-if="model.isDisplayNamesDifferent"
          />
          <el-input v-model="model.display_name" class="display_name" v-else />
        </template>
      </rest-list-column>

      <rest-list-column-enum
        prop="type"
        :editable="true"
        :enum="consentPreferenceType"
        :title="$t('SubscriptionPreference_Field_Type')"
        :width="200"
        :number="true"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <enum-select
            v-model="model.type"
            :options="consentPreferenceType"
            :number="true"
            :disabled="!!model.id"
          />
        </template>
      </rest-list-column-enum>

      <rest-list-column
        prop="channel"
        :min-width="channelsWidth"
        :editable="true"
        :filterable="false"
        :filter-input="false"
        :sortable="false"
        :title="$t('SubscriptionPreference_Field_Channel')"
      >
        <template slot-scope="{ scope }" slot="default">
          <div class="channels-checkboxes-wrapper-disabled">
            <div class="channels-checkboxes">
              <checkbox
                :key="channel.key"
                disabled
                :title="$t(channel.name)"
                :value="scope.row.channelList[channel.key]"
                v-for="channel in channelsList"
              />
            </div>
          </div>
        </template>
        <template slot="input-edit" slot-scope="{ model }">
          <div class="channels-checkboxes-wrapper-editable">
            <div class="channels-checkboxes">
              <checkbox
                :key="channel.key"
                :title="$t(channel.name)"
                v-model="model.channelList[channel.key]"
                @input="checkboxChange"
                v-for="channel in channelsList"
              />
            </div>
          </div>
        </template>
      </rest-list-column>

      <rest-list-column-actions
        v-if="!readonly"
        :editable="true"
        :fixed="true"
        fixed="right"
      >
        <template slot-scope="scope">
          <rest-list-action-delete
            :endpoint="deleteEndpoint(scope.row)"
            :name="scope.row.name"
          />
        </template>
      </rest-list-column-actions>
    </rest-list-editable>
  </form-section>
</template>

<script>
import RestListColumn from '@/common/components/rest/RestListColumn'
import { projectEndpoint } from '@/core/helpers'
import { SubscriptionPreferenceChannelLabels } from '@/modules/settings/enum/SubscriptionPreferenceChannelEnum'
import RestListEditable from '@/common/components/rest/RestListEditable'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import { EventBus } from '@/core/EventBus'
import Checkbox from '@/common/components/form/Checkbox'
import RestApi from '@/core/services/RestApi'
import FormSection from '@/common/components/form/FormSection'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import { consentPreferenceType } from '@/modules/consent-manager/enum/ConsentPreferenceEnums'
import EnumSelect from '@/common/components/form/EnumSelect'

export default {
  name: 'ConsentManagerSubscriptionPreferencesPage',
  components: {
    FormSection,
    Checkbox,
    RestListActionDelete,
    RestListColumnActions,
    RestListEditable,
    RestListColumn,
    RestListColumnEnum,
    EnumSelect
  },
  mounted() {
    EventBus.$on('add-subscription-preference-row', this.addRow)
    RestApi(this.channelsEndpoint)
      .then(({ data }) => {
        this.consentChannels = data
        setTimeout(() => {
          this.channelsColumnWidth = document
            .querySelector('.channels-checkboxes')
            .getBoundingClientRect().width
        }, 500)
      })
      .catch(error => {
        this.$message({
          message: error.message,
          type: 'error'
        })
        this.loading = false
      })
  },
  destroyed() {
    EventBus.$off('add-subscription-preference-row')
  },
  data() {
    return {
      SubscriptionPreferenceChannelLabels,
      consentPreferenceType,
      consentChannels: [],
      channelsUpdated: false,
      channelsColumnWidth: null,
      tempDisplayName: ''
    }
  },
  computed: {
    displayName: {
      get() {
        return this.tempDisplayName
      },
      set(val) {
        this.tempDisplayName = val
        let row = this.$refs['rest-list-editable'].editableRow
        this.$set(row, 'display_name', val)
      }
    },
    rules() {
      return {
        name: [
          {
            required: true,
            trigger: 'blur',
            message: this.$t('SubscriptionPreference_Field_Name_Required')
          },
          {
            max: 100,
            trigger: 'blur',
            message: this.$t('SubscriptionPreference_Field_Name_TooLong')
          },
          {
            validator: this.validatePreferenceName
          }
        ],
        display_name: {
          max: 100,
          trigger: 'blur',
          message: this.$t('SubscriptionPreference_Field_DisplayName_TooLong')
        },
        type: {
          required: true,
          trigger: 'blur',
          message: this.$t('SubscriptionPreference_Field_Type_Required')
        }
      }
    },

    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    pageBreadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('Settings_Breadcrumb_SubscriptionPreferences')
        }
      ]
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `consent-subscription-preferences`,
        params: {
          sort: 'name'
        }
      })
    },

    readonly() {
      return !this.$can('SubscriptionPreference::Update')
    },

    channelsEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `consent-channels`,
        params: {
          sort: 'name'
        }
      })
    },

    staticChannels() {
      if (!this.SubscriptionPreferenceChannelLabels) return []
      return Object.keys(this.SubscriptionPreferenceChannelLabels).map(key => {
        return { key: key, name: this.SubscriptionPreferenceChannelLabels[key] }
      })
    },

    channelsList() {
      return [...this.staticChannels, ...this.consentChannels]
    },

    channelsWidth() {
      if (this.channelsColumnWidth) return this.channelsColumnWidth + 20
      let lettersCount = 0
      this.channelsList.forEach(c => {
        lettersCount += c.name.length
      })
      return this.channelsList.length * 30 + lettersCount * 7.9
    }
  },

  methods: {
    handleTabKey(e) {
      if (e.keyCode === 9) {
        document.querySelector('.display_name input').focus()
        e.preventDefault()
      }
    },
    beforeSaveRow(row) {
      if (row.isDisplayNamesDifferent) {
        row.display_name = ''
      }
    },

    onRowUpdated() {
      this.channelsUpdated = false
    },

    onRowDiscard() {
      this.tempDisplayName = ''
    },

    checkboxChange() {
      this.channelsUpdated = true
    },

    addEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `consent-subscription-preferences/save-subscription`
      })
    },

    updateEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `consent-subscription-preferences/save-subscription`
      })
    },

    deleteEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `consent-subscription-preferences/remove-subscription`,
        data: { name: row.name }
      })
    },

    addRow() {
      this.$refs['rest-list-editable'].addRow({
        name: '',
        display_name: '',
        channelList: {}
      })
    },

    validatePreferenceName(rule, value, callback) {
      let row = this.$refs['rest-list-editable'].editableRow
      if (
        row.hasOwnProperty('id') ||
        /^[A-Za-z0-9 _~\-!@#$%^&*()]+$/.test(value)
      ) {
        callback()
        return
      }

      callback(new Error(this.$t('SubscriptionPreferences_Invalid_Name')))
    }
  }
}
</script>

<style scoped>
.ConsentNameInfo--Tooltip {
  max-width: 300px;
}

.channels-checkboxes {
  display: inline-flex;
}
.channels-checkboxes-wrapper-disabled {
  display: flex;
}
.channels-checkboxes-wrapper-editable {
  display: block;
  padding-top: 5.5px;
}
>>> .el-checkbox {
  padding: 0 !important;
}
>>> .channels-checkboxes .is-disabled.is-checked .el-checkbox__inner {
  background-color: #a1a3a7 !important;
  border-color: #878282ff !important;
}
>>> .channels-checkboxes .is-disabled .el-checkbox__inner,
>>> .channels-checkboxes .is-disabled .el-checkbox__inner::after,
>>> .channels-checkboxes .is-disabled .el-checkbox__label {
  cursor: default !important;
}

>>> .el-table__fixed-body-wrapper {
  z-index: 51 !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/consent-manager/pages/ConsentManagerSubscriptionPreferencesPage.vue